import moment from "moment";
import {
  StickerSearchItem,
  StickerSortBy,
  filterSortedStickers,
  getStickerItemKey,
  getStickerName,
  sortStickerCollectionOnce,
} from "./stickerTabsSearch";

export type OwnedCollectionItem = StickerSearchItem & {
  id?: string | number;
  product_description?: string;
  status?: string | number;
  user?: { user_name?: string };
};

export const OWNED_COLLECTION_PAGE_SIZE = 50;
export const OWNED_COLLECTION_KEYSTROKE_BUDGET_MS = 50;
export const OWNED_COLLECTION_MAX_VISIBLE_ROWS = 60;

export const getOwnedCollectionItems = (collection: any): OwnedCollectionItem[] => {
  if (Array.isArray(collection?.store)) {
    return collection.store;
  }
  return [];
};

export const getOwnedCollectionCreditBalance = (collection: any) => {
  if (collection && !Array.isArray(collection)) {
    return collection.credit_balance;
  }
  return undefined;
};

/** Mirrors the current MyCollection.tsx render-path sort (mutates + moment). */
export const sortOwnedCollectionLegacy = (
  items: OwnedCollectionItem[],
  sortedBy: StickerSortBy
) => {
  if (!items?.length) {
    return [];
  }
  return items.sort((a, b) => {
    if (sortedBy === "name") {
      const nameA = a?.product_name ?? "";
      const nameB = b?.product_name ?? "";
      return nameA.localeCompare(nameB);
    }
    return moment.utc(b?.createdon).diff(moment.utc(a?.createdon));
  });
};

export const sortOwnedCollectionOnce = (
  items: OwnedCollectionItem[],
  sortedBy: StickerSortBy
) => sortStickerCollectionOnce(items, sortedBy);

export const filterOwnedCollection = (
  sortedItems: OwnedCollectionItem[],
  filter: string
) => filterSortedStickers(sortedItems, filter);

export const paginateOwnedCollection = (
  items: OwnedCollectionItem[],
  page: number,
  pageSize = OWNED_COLLECTION_PAGE_SIZE
) => {
  const safePageSize = Math.max(1, pageSize);
  const total = items.length;
  const pageCount = Math.max(1, Math.ceil(total / safePageSize));
  const currentPage = Math.min(Math.max(1, page), pageCount);
  const start = (currentPage - 1) * safePageSize;
  return {
    items: items.slice(start, start + safePageSize),
    page: currentPage,
    pageCount,
    start: total === 0 ? 0 : start + 1,
    end: Math.min(total, start + safePageSize),
    total,
  };
};

export const getOwnedCollectionRowKey = (
  item: OwnedCollectionItem,
  index: number
) => {
  const id = String(item?.id ?? "").trim();
  if (id) {
    return id;
  }
  return getStickerItemKey(item, index);
};

export const getOwnedCollectionRowKeys = (items: OwnedCollectionItem[]) => {
  const seen = new Map<string, number>();
  return items.map((item, index) => {
    const base = getOwnedCollectionRowKey(item, index);
    const count = seen.get(base) ?? 0;
    seen.set(base, count + 1);
    return count === 0 ? base : `${base}#${count}`;
  });
};

export const matchesOwnedCollectionQuery = (
  item: OwnedCollectionItem,
  filter: string
) => {
  const query = String(filter || "").trim().toLowerCase();
  if (!query) {
    return true;
  }
  return getStickerName(item).toLowerCase().includes(query);
};
